/**
 * @file CustomFieldsTab.js
 * @description This component displays custom fields of an individual in two columns with ascending and descending orders respectively.
 */

import {
    useRef,
    useState,
    useLayoutEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import style from './IndividualPage.module.scss';
import Toast from '../ui/toasts/Toast';
import { updateIndividual } from '../../services/individualService';
import { isValidMoney } from '../../utils/validate';
import { TenantUserStatusEnum } from '../../utils/data/enums';
import CustomFieldInput from '../shared/CustomFieldInput';
import { getText } from '../../i18n';

/**
 * Checks if the account is disabled and shows toast message if so
 * @param {Object} tenantUser - The tenant user object
 * @returns {boolean} True if account is disabled
 */
const checkAccountDisabled = tenantUser => {
    if (tenantUser?.tenantUserStatusId === TenantUserStatusEnum.Disabled) {
        Toast.error(
            getText('msg_66aa8e9b0915'),
            {},
            getText('msg_8d884d6c8bcb'),
        );
        return true;
    }
    return false;
};

const CustomFieldsTab = forwardRef(
    (
        {
            individualId,
            individualRecord,
            tenantUser,
            onSave,
            fieldNamingMap,
            defaultFieldNamingMap,
        },
        ref,
    ) => {
        const [customFieldsData, setCustomFieldsData] = useState([]);
        const [isLoading, setIsLoading] = useState(false);

        const labelRefs = useRef([]);

        useImperativeHandle(ref, () => ({
            handleSave,
        }));

        useLayoutEffect(() => {
            const maxWidth = labelRefs.current.reduce((max, current) => {
                return current ? Math.max(max, current.offsetWidth) : max;
            }, 0);

            labelRefs.current.forEach(ref => {
                if (ref) ref.style.width = `${maxWidth}px`;
            });
        }, [customFieldsData]);

        useLayoutEffect(() => {
            const extractCustomFields = () => {
                const customFields = Object.entries(individualRecord)
                    .filter(([key]) => key.startsWith('custom'))
                    .map(([fieldName, value]) => ({ fieldName, value }));

                setCustomFieldsData(customFields);
            };

            extractCustomFields();
        }, [individualRecord]);

        const validate = individualRecord => {
            const moneyFields = [
                'customMoney1',
                'customMoney2',
                'customMoney3',
                'customMoney4',
                'customMoney5',
            ];

            for (const field of moneyFields) {
                if (individualRecord[field]) {
                    const errorMessage = isValidMoney(
                        individualRecord[field],
                        field.replace(/([A-Z])/g, ' $1').trim(),
                    );
                    if (errorMessage) return errorMessage;
                }
            }

            return '';
        };

        const handleInputChange = (fieldName, updatedValue) => {
            setCustomFieldsData(currentFields =>
                currentFields.map(field =>
                    field.fieldName === fieldName
                        ? {
                              ...field,
                              value:
                                  updatedValue === '' || updatedValue === null
                                      ? null
                                      : updatedValue,
                          }
                        : field,
                ),
            );
        };

        const handleSave = async event => {
            event?.preventDefault();

            if (checkAccountDisabled(tenantUser)) return;

            try {
                setIsLoading(true);
                const payload = {
                    ...individualRecord,
                };

                for (let val of Object.keys(customFieldsData)) {
                    const field = customFieldsData[val];
                    payload[field.fieldName] = field.value;
                }

                var errorMessage = validate(payload);

                if (errorMessage) {
                    Toast.error(errorMessage);
                    return;
                }

                await updateIndividual(payload);
                Toast.success(getText('msg_f11994b03b86'));
                onSave();
            } catch (error) {
                console.error('Unable to save changes', error);
                if (error?.response?.data?.detail)
                    Toast.error(error?.response?.data?.detail);
                else Toast.error(getText('msg_3c2ce6f3c1a9'));
            } finally {
                setIsLoading(false);
            }
        };

        if (customFieldsData.length === 0) {
            return <p>{getText('msg_63f815a393d5')}</p>;
        }

        const assignRef = (ref, index) => {
            labelRefs.current[index] = ref;
        };

        const renderField = (field, index) => {
            const formatFieldName = fieldName => {
                // Split the fieldName into prefix and suffix based on the boundary between letters and digits
                const [prefix, suffix] = `${fieldName}`.split(/(?<=\D)(?=\d)/);

                // Insert spaces before uppercase letters and trim any leading/trailing spaces
                let formattedPrefix = prefix.replace(/([A-Z])/g, ' $1').trim();

                // Capitalize the first letter of the formattedPrefix
                formattedPrefix =
                    formattedPrefix.charAt(0).toUpperCase() +
                    formattedPrefix.slice(1);

                // Return the formatted prefix and suffix combined with a space
                return `${formattedPrefix} ${suffix}`;
            };

            let formattedFieldName = formatFieldName(field.fieldName);

            const isDateField = formattedFieldName
                .toLowerCase()
                .includes('date');
            const isMoneyField = formattedFieldName
                .toLowerCase()
                .includes('money');
            const isTextField = formattedFieldName
                .toLowerCase()
                .includes('text');

            let subLabel = '';
            const key = defaultFieldNamingMap[formattedFieldName];
            if (
                key &&
                fieldNamingMap[key] &&
                fieldNamingMap[key] !== formattedFieldName
            ) {
                subLabel = `(${formattedFieldName})`;
                formattedFieldName = fieldNamingMap[key];
            }

            return (
                <li key={field.fieldName}>
                    <CustomFieldInput
                        field={field}
                        isDateField={isDateField}
                        isMoneyField={isMoneyField}
                        isTextField={isTextField}
                        fieldName={formattedFieldName}
                        handleInputChange={handleInputChange}
                        assignRef={ref => assignRef(ref, index)}
                        subLabel={subLabel}
                        styles={{
                            labelContainer: style.labelContainer,
                            inputLabel: style.inputLabel,
                            inputField: style.customInputField,
                        }}
                    />
                </li>
            );
        };

        const totalFields = customFieldsData.length;

        return (
            <>
                <form className={style.customFieldContainer}>
                    <div className="row">
                        <ul
                            className={`col-12 col-sm-12 col-md-12 col-lg-12 ${style.customList}`}
                        >
                            {customFieldsData
                                .slice(0, totalFields)
                                .map(renderField)}
                        </ul>
                    </div>
                </form>
            </>
        );
    },
);

export default CustomFieldsTab;
